/**
 * Helper murni untuk editor isi Knowledge Base.
 *
 * Tidak mengimpor modul lain agar dapat diuji langsung tanpa DOM dan tanpa
 * membawa dependency Vuex/axios. Semua aturan progres mengikuti backend:
 * persentase hanya boleh 100 ketika status job benar-benar `success`.
 */

export const KB_EDITOR_CHUNK_MAX_CHARS = 1000;
export const KB_EDITOR_ACTIVE_STATUSES = ["queued", "processing", "finalizing"];

const STAGE_LABELS = {
  extracting: "Ekstraksi teks",
  splitting: "Pembagian bagian",
  embedding: "Embedding",
  storing_qdrant: "Penyimpanan Qdrant",
  finalizing: "Finalisasi",
  done: "Selesai"
};

export function kbEditorCharCount(value) {
  // Array.from menghitung code point (bukan unit UTF-16) sehingga emoji dan
  // karakter Unicode dihitung sama seperti backend (len([]rune)).
  return Array.from(String(value === null || value === undefined ? "" : value)).length;
}

export function kbEditorIsDirty(original, draft) {
  const before = String(original === null || original === undefined ? "" : original);
  const after = String(draft === null || draft === undefined ? "" : draft);
  return before !== after;
}

export function kbEditorValidateText(value) {
  const text = String(value === null || value === undefined ? "" : value);
  if (!text.trim()) return "Isi dokumen tidak boleh kosong.";
  return "";
}

export function kbEditorNormalizeStatus(value) {
  return String(value || "").trim().toLowerCase();
}

export function kbEditorIsActiveStatus(status) {
  return KB_EDITOR_ACTIVE_STATUSES.indexOf(kbEditorNormalizeStatus(status)) !== -1;
}

export function kbEditorStageLabel(stage) {
  const key = kbEditorNormalizeStatus(stage);
  return STAGE_LABELS[key] || (key ? key : "");
}

export function kbEditorProgressPercent(job) {
  if (!job) return 0;
  const status = kbEditorNormalizeStatus(job.status);
  if (status === "success") return 100;
  const raw = job.progress && job.progress.percent !== undefined ? job.progress.percent : 0;
  const percent = Math.max(0, Math.min(100, Number(raw) || 0));
  return percent >= 100 ? 99 : percent;
}

export function kbEditorProgress(job) {
  const progress = (job && job.progress) || {};
  return {
    total: Number(progress.total) || 0,
    queued: Number(progress.queued) || 0,
    processing: Number(progress.processing) || 0,
    embedded: Number(progress.embedded) || 0,
    completed: Number(progress.completed) || 0,
    processed:
      progress.processed === undefined
        ? Number(progress.completed) || 0
        : Number(progress.processed) || 0,
    failed: Number(progress.failed) || 0,
    percent: kbEditorProgressPercent(job)
  };
}

/**
 * Menentukan apakah GET content menunjukkan proses yang benar-benar aktif.
 * `pending_job_id` saja tidak cukup karena bisa menunjuk job terminal (stale).
 * Dokumen legacy tanpa job harus tetap dapat dibuka di editor.
 */
export function kbEditorContentIsProcessing(data) {
  if (!data) return false;
  if (data.is_processing === true) return true;
  if (data.pending_job_active === true) return true;
  return false;
}

/** Job yang diharapkan sedang berjalan untuk dokumen ini. */
export function kbEditorExpectedJobId(data) {
  if (!data) return "";
  return String(data.running_job_id || data.pending_job_id || "");
}

export function kbEditorCanEdit(job, docStatusKey) {
  if (job && kbEditorIsActiveStatus(job.status)) return false;
  if (docStatusKey === "processing" || docStatusKey === "pending") return false;
  return true;
}

export function kbEditorEditDisabledReason(job, docStatusKey) {
  if (job && kbEditorIsActiveStatus(job.status)) {
    return "Dokumen sedang diproses. Edit Isi tersedia setelah proses selesai.";
  }
  if (docStatusKey === "processing" || docStatusKey === "pending") {
    return "Dokumen sedang menunggu atau dalam proses. Edit Isi tersedia setelah proses selesai.";
  }
  return "";
}

export function kbEditorHttpError(error) {
  if (!error) return { status: 0, message: "" };
  const response = error.response || {};
  const payload = response.data || error.data || {};
  const status = Number(response.status) || Number(payload.status) || 0;
  const message = String(payload.msg || error.message || error.msg || "").replace(
    /<br\s*\/?>/gi,
    " "
  );
  return { status: status, message: message };
}

export function kbEditorContentError(error) {
  const info = kbEditorHttpError(error);
  if (info.status === 401) {
    return { kind: "auth", message: info.message || "Sesi login tidak valid. Muat ulang halaman." };
  }
  if (info.status === 403) {
    return {
      kind: "forbidden",
      message: info.message || "Anda tidak memiliki akses ke dokumen ini."
    };
  }
  if (info.status === 404) {
    return {
      kind: "not_found",
      message: info.message || "Dokumen Knowledge Base tidak ditemukan."
    };
  }
  if (info.status === 422) {
    return {
      kind: "extraction",
      message:
        info.message ||
        "Ekstraksi dokumen gagal. Dokumen perlu diperiksa atau diunggah ulang."
    };
  }
  if (info.status === 502) {
    return {
      kind: "source_missing",
      message:
        (info.message || "File sumber dokumen tidak tersedia di penyimpanan.") +
        " Unggah ulang dokumen untuk mengedit isinya."
    };
  }
  if (!info.status) {
    return {
      kind: "network",
      message: "Tidak dapat menghubungi server. Periksa koneksi lalu coba lagi.",
    };
  }
  return {
    kind: "server",
    message: info.message || "Isi dokumen belum berhasil dimuat.",
  };
}

export function kbEditorSaveError(error) {
  const info = kbEditorHttpError(error);
  if (info.status === 409) {
    return {
      kind: "conflict",
      message:
        info.message ||
        "Dokumen sedang diproses atau versi konten sudah berubah. Muat ulang isi terbaru."
    };
  }
  if (info.status === 400 || info.status === 422) {
    return {
      kind: "validation",
      message: info.message || "Isi dokumen tidak valid."
    };
  }
  if (info.status === 502) {
    return {
      kind: "source_missing",
      message:
        info.message ||
        "Layanan terkait sedang bermasalah. Coba lagi beberapa saat lagi."
    };
  }
  if (!info.status) {
    return {
      kind: "network",
      message:
        "Tidak dapat memastikan job dibuat. Periksa status pemrosesan terbaru sebelum mengirim ulang.",
    };
  }
  return {
    kind: "server",
    message: info.message || "Gagal menjadwalkan pembaruan dokumen."
  };
}

export function kbEditorIdempotencyKey() {
  return (
    "kb-edit-" +
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2, 10)
  );
}

export function kbEditorJobMatchesContentVersion(job, contentVersion) {
  if (!job) return false;
  const jobVersion = Number(job.content_version) || 0;
  const expected = Number(contentVersion) || 0;
  if (!jobVersion) return true;
  return jobVersion >= expected;
}



// WEBPACK FOOTER //
// ./src/utils/knowledgeBaseEditor.js